/**
 * NotificationProvider — IMPL-AUTH-SIGNIN-SILENT-REJECTION-001 (Phase A, R-1)
 *
 * Route-agnostic toast provider. Mounts above every App.jsx early-return so
 * showNotification callers surface feedback regardless of which render branch
 * is active. Preserves the pre-hoist (message, type) contract and 3s
 * auto-dismiss; adds WCAG 2.2 AA role/aria-live attributes.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

const DISMISS_MS = 3000;

const NotificationContext = createContext(null);

export function NotificationProvider({ children }) {
  const [notification, setNotification] = useState(null);
  const timeoutRef = useRef(null);

  const showNotification = useCallback((message, type = 'success') => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setNotification({ message, type });
    timeoutRef.current = setTimeout(() => {
      setNotification(null);
      timeoutRef.current = null;
    }, DISMISS_MS);
  }, []);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);

  const value = useMemo(() => ({ showNotification }), [showNotification]);

  const isError = notification?.type === 'error';

  return (
    <NotificationContext.Provider value={value}>
      {children}
      {notification && (
        <div
          role={isError ? 'alert' : 'status'}
          aria-live={isError ? 'assertive' : 'polite'}
          aria-atomic="true"
          className={`fixed top-4 right-4 z-50 notification-enter px-6 py-3 rounded-lg shadow-xl ${
            isError ? 'bg-red-600' : 'bg-orange-500'
          } text-white font-semibold`}
        >
          {notification.message}
        </div>
      )}
    </NotificationContext.Provider>
  );
}

export function useNotification() {
  const ctx = useContext(NotificationContext);
  if (ctx === null) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return ctx;
}
